import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GetCenterChartFieldType, GetCenterChartResponse, GetTsabtChartParams, } from '@data';
import { ApiService } from './api.service';
import {
  GET_TSABT_STATS_ENDPOINT,
  GET_TSABTS_STATS_ENDPOINT,
  GET_TSBAT_CHART_ENDPOINT,
} from '../constants/endpoints.constants';
import { Observable } from 'rxjs';
import {
  GetTsabtsStatsParams,
  GetTsabtStatsFieldType,
  GetTsabtStatsParams,
  GetTsabtStatsResponse,
} from '../models/tsabts.models';
import { withSpinner } from '../../app/core/services/spinner.controller';

/**
 * DataTsabtsService provides methods for interacting with TSABTs data through the API.
 * This service handles operations such as fetching TSABT chart data.
 */
@Injectable({ providedIn: 'root' })
export class DataTsabtsService {
  protected readonly apiService = inject(ApiService);

  /**
   * Retrieves chart data for TSABTs based on specified parameters.
   *
   * @param params - An object containing:
   *   - `fields`: An array of field types or a string specifying the chart fields.
   *   - Other parameters as per `GetTsabtChartParams`, excluding `fields`.
   * @returns An `Observable` of `GetCenterChartResponse` containing the chart data.
   */
  public getTsabtChart(
    params: Omit<GetTsabtChartParams, 'fields'> & {
      fields: GetCenterChartFieldType[] | string;
    },
  ): Observable<GetCenterChartResponse> {
    if (Array.isArray(params.fields)) {
      params.fields = params.fields.join(',');
    }
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetCenterChartResponse>(GET_TSBAT_CHART_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Retrieves statistical data for TSABTs based on the specified parameters.
   *
   * @param params - An object containing:
   *   - `fields`: An array of field types or a string specifying the stats fields.
   *   - Other parameters as per `GetTsabtStatsParams`, excluding `fields`.
   * @returns An `Observable` of `GetTsabtStatsResponse` containing the statistical data.
   */
  public getTsbatStats(
    params: Omit<GetTsabtStatsParams, 'fields'> & {
      fields: GetTsabtStatsFieldType[] | string;
    },
  ): Observable<GetTsabtStatsResponse> {
    if (Array.isArray(params.fields)) {
      params.fields = params.fields.join(',');
    }
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetTsabtStatsResponse>(GET_TSABT_STATS_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  public getTsbatsStats(
    params: Omit<GetTsabtsStatsParams, 'fields'> & {
      fields: GetTsabtStatsFieldType[] | string;
    },
  ): Observable<GetTsabtStatsResponse> {
    if (Array.isArray(params.fields)) {
      params.fields = params.fields.join(',');
    }
    if (Array.isArray(params.tsabtIds)) {
      params.tsabtIds = params.tsabtIds.join(',');
    }
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetTsabtStatsResponse>(GET_TSABTS_STATS_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }
}
